"use client";

import { memo, useEffect, useState, type CSSProperties } from "react";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";
import { useCanvasResourceResolver } from "../resource-resolver";
import { resolveEnum } from "./utils";

const BEEOS_FILE_PREFIX = "beeos-file://";

const HINT_CLASSES: Record<string, string> = {
  icon: "h-6 w-6 shrink-0",
  avatar: "h-10 w-10 rounded-full shrink-0",
  smallFeature: "w-24 rounded-md",
  mediumFeature: "w-full max-w-sm rounded-md",
  largeFeature: "w-full rounded-lg",
  header: "w-full rounded-lg",
};

const FIT_MAP: Record<string, string> = {
  contain: "object-contain",
  cover: "object-cover",
  fill: "object-fill",
  none: "object-none",
  "scale-down": "object-scale-down",
};

function isBeeosFileUrl(url: string): boolean {
  return url.startsWith(BEEOS_FILE_PREFIX);
}

function ImageFallback({
  className,
  label,
}: {
  className: string;
  label: string;
}) {
  return (
    <div
      className={`flex min-h-24 items-center justify-center border border-dashed border-border/50 bg-muted/20 text-xs text-muted-foreground ${className}`}
      role="status"
    >
      {label}
    </div>
  );
}

export const BeeOSImage = memo(function BeeOSImage({
  node,
  surfaceId,
}: A2UIComponentProps<Types.ImageNode>) {
  const { resolveString } = useA2UIComponent(node, surfaceId);
  const resolveResource = useCanvasResourceResolver();
  const props = node.properties;
  const url = resolveString(props.url);
  const extProps = props as typeof props & { altText?: unknown };
  const altText = resolveString(extProps.altText as Parameters<typeof resolveString>[0]);
  const usageHint = resolveEnum<string>(props.usageHint, "");
  const fit = resolveEnum(props.fit, "fill");
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [resolveState, setResolveState] = useState<"idle" | "loading" | "unavailable">("idle");
  const [imageFailed, setImageFailed] = useState(false);

  const needsResolve = !!url && isBeeosFileUrl(url);

  useEffect(() => {
    setResolvedUrl(null);
    setImageFailed(false);

    if (!url || !needsResolve) {
      setResolveState("idle");
      return;
    }

    if (!resolveResource) {
      setResolveState("unavailable");
      return;
    }

    let cancelled = false;
    setResolveState("loading");
    resolveResource(url)
      .then((resource) => {
        if (cancelled) return;
        const nextUrl = resource?.previewUrl || resource?.downloadUrl || null;
        setResolvedUrl(nextUrl);
        setResolveState(nextUrl ? "idle" : "unavailable");
      })
      .catch(() => {
        if (!cancelled) setResolveState("unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, [needsResolve, resolveResource, url]);

  if (!url) return null;

  const hostStyle: CSSProperties =
    node.weight !== undefined ? ({ "--weight": node.weight } as CSSProperties) : {};

  const hintClass = usageHint ? HINT_CLASSES[usageHint] ?? "" : "w-full rounded-md";
  const fitClass = FIT_MAP[fit] ?? "object-fill";
  const displayUrl = needsResolve ? resolvedUrl : url;

  if (!displayUrl || imageFailed) {
    return (
      <div className="a2ui-image" style={hostStyle}>
        <ImageFallback
          className={hintClass}
          label={resolveState === "loading" ? "Loading image..." : "Image unavailable"}
        />
      </div>
    );
  }

  return (
    <div className="a2ui-image" style={hostStyle}>
      <img
        src={displayUrl}
        alt={altText || ""}
        className={`h-auto ${hintClass} ${fitClass}`}
        onError={() => {
          if (needsResolve) setImageFailed(true);
        }}
      />
    </div>
  );
});
